import React, { useState, useEffect } from "react";
import { ChevronDown, Eye, EyeOff } from "lucide-react";
import { getAccountTypes } from "../../api/accountTypeApi";
import { getStatusEmployee } from "../../api/employeeApi";
import { getProvinces, getRegencies } from "../../api/publicApi";
import AuthHeader from "./AuthHeader";  
import AuthFooter from "./AuthFooter";

const RegisterForm = () => {
  const [accountTypes, setAccountTypes] = useState([]);
  const [employeeStatuses, setEmployeeStatuses] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingCity, setLoadingCity] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedProvince, setSelectedProvince] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    let isMounted = true;

    getAccountTypes().then((data) => {
      if (isMounted) {
        setAccountTypes(data);
        setLoading(false);
      }
    });

    getStatusEmployee().then((data) => {
      if (isMounted) {
        setEmployeeStatuses(data);
        setLoadingStatus(false);
      }
    });

    getProvinces().then((data) => {
      if (isMounted) setProvinces(data);
    });

    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    if (selectedProvince) {
      setLoadingCity(true);
      getRegencies(selectedProvince).then((data) => {
        setCities(data);
        setLoadingCity(false);
      });
    } else {
      setCities([]);
    }
  }, [selectedProvince]);

  const handleProvinceChange = (e) => {
    setSelectedProvince(e.target.value);
  };

  return (
    <div class="bg-white dark:bg-slate-800 p-10 md:p-14 rounded-[1rem] shadow-2xl w-full max-w-[1400px] border border-slate-100 dark:border-white/5 transition-all mx-auto">
      {/* Header*/}
      <AuthHeader 
        title="Create Account" 
        description="Lengkapi data diri Anda di bawah ini untuk mendaftar sebagai peserta resmi." 
      />
  
      {/*Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-4">
        {/* LEFT: FORM SECTION */}
        <div className="lg:col-span-8 space-y-8">
          <h2 className="text-2xl font-black text-[#003366] uppercase tracking-tighter">Create Account</h2>
          
          <form className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Account Type */}
            <div className="md:col-span-2">
              <label className="block text-[10px] font-black text-slate-800 mb-2 uppercase tracking-widest">Select Account Type *</label>
              <div className="relative">
                <select disabled={loading} className="w-full appearance-none bg-white border border-slate-200 rounded-lg p-3 text-xs font-medium focus:ring-2 focus:ring-blue-500/20 outline-none transition-all cursor-pointer disabled:bg-slate-50">
                  <option value="">{loading ? "Loading..." : "--Select--"}</option>
                  {!loading && accountTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
              </div>
            </div>

            {/* Names */}
            <div className="md:col-span-2">
              <label className="block text-[10px] font-black text-slate-800 mb-2 uppercase tracking-widest">First Name *</label>
              <input type="text" placeholder="Insert First Name" className="w-full bg-white border border-slate-200 rounded-lg p-3 text-xs outline-none focus:border-blue-500 transition-all" />
            </div>

            <div className="md:col-span-1">
              <label className="block text-[10px] font-black text-slate-800 mb-2 uppercase tracking-widest">Middle Name</label>
              <input type="text" placeholder="Insert Middle" className="w-full bg-white border border-slate-200 rounded-lg p-3 text-xs outline-none focus:border-blue-500 transition-all" />
            </div>
            <div className="md:col-span-1">
              <label className="block text-[10px] font-black text-slate-800 mb-2 uppercase tracking-widest">Last Name</label>
              <input type="text" placeholder="Insert Last" className="w-full bg-white border border-slate-200 rounded-lg p-3 text-xs outline-none focus:border-blue-500 transition-all" />
            </div>

            {/* NIK */}
            <div className="md:col-span-2">
              <label className="block text-[10px] font-black text-slate-800 mb-2 uppercase tracking-widest">NIK/Nomor KTP *</label>
              <input type="text" placeholder="Masukkan NIK/Nomor KTP" className="w-full bg-white border border-slate-200 rounded-lg p-3 text-xs outline-none focus:border-blue-500 transition-all" />
              <p className="text-[9px] text-blue-600 font-medium italic mt-2 leading-relaxed">
                Data NIK diperlukan hanya untuk kebutuhan sinkronisasi data dengan Plataran Sehat.
              </p>
            </div>

            {/* Siloam Group Dropdown */}
            <div className="md:col-span-2">
              <label className="block text-[10px] font-black text-slate-800 mb-2 uppercase tracking-widest leading-tight">
                Apakah Anda Dokter / Perawat Siloam Group *
              </label>
              <div className="relative">
                <select 
                  disabled={loadingStatus}
                  name="isSiloamGroup"
                  className="w-full appearance-none bg-white border border-slate-200 rounded-lg p-3 text-xs font-medium focus:ring-2 focus:ring-blue-500/20 outline-none transition-all cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed"
                >
                  <option value="">{loadingStatus ? "Loading..." : "--Pilih--"}</option>
                  {!loadingStatus && employeeStatuses.map((status) => (
                    <option key={status.id} value={status.id}>{status.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
              </div>
            </div>

            {/* Province */}
            <div className="md:col-span-1">
              <label className="block text-[10px] font-black text-slate-800 mb-2 uppercase tracking-widest">Province *</label>
              <div className="relative">
                <select 
                  onChange={handleProvinceChange}
                  className="w-full appearance-none bg-white border border-slate-200 rounded-lg p-3 text-xs font-medium focus:ring-2 focus:ring-blue-500/20 outline-none transition-all cursor-pointer"
                >
                  <option value="">--Select Province--</option>
                  {provinces.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
              </div>
            </div>

            {/* City */}
            <div className="md:col-span-1">
              <label className="block text-[10px] font-black text-slate-800 mb-2 uppercase tracking-widest">City *</label>
              <div className="relative">
                <select 
                  disabled={!selectedProvince || loadingCity}
                  className="w-full appearance-none bg-white border border-slate-200 rounded-lg p-3 text-xs font-medium focus:ring-2 focus:ring-blue-500/20 outline-none transition-all cursor-pointer disabled:bg-slate-50"
                >
                  <option value="">{loadingCity ? "Loading..." : "--Select City--"}</option>
                  {cities.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
              </div>
            </div>

            {/* Phone Number */}
            <div className="md:col-span-2">
              <label className="block text-[10px] font-black text-slate-800 mb-2 uppercase tracking-widest">
                Phone Number *
              </label>
              <div className="relative group">
                {/* Awalan Kode Negara +62 (Static) */}
                <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 border-r border-slate-200 pr-2">
                  <span className="text-[10px] font-bold text-slate-500">+62</span>
                </div>
                
                <input 
                  type="tel" 
                  name="phone"
                  placeholder="812-3456-7890" 
                  pattern="[0-9]*"
                  className="w-full bg-white border border-slate-200 rounded-lg p-3 pl-14 text-xs outline-none focus:border-blue-500 transition-all placeholder:text-slate-300"
                  onChange={(e) => {
                    // Hanya izinkan angka
                    const value = e.target.value.replace(/\D/g, "");
                    e.target.value = value;
                  }}
                />
              </div>
              <p className="text-[9px] text-slate-400 font-medium italic mt-2">
                Contoh: 81362443808 (Tanpa angka 0 di depan)
              </p>
            </div>

            {/* Email */}
            <div className="md:col-span-2">
              <label className="block text-[10px] font-black text-slate-800 mb-2 uppercase tracking-widest">
                Email * <span className="text-blue-600 italic">(Gunakan email Plataran Sehat)</span>
              </label>
              <input type="email" placeholder="Masukkan alamat email" className="w-full bg-white border border-slate-200 rounded-lg p-3 text-xs outline-none focus:border-blue-500 transition-all" />
            </div>

          {/* Password */}
          <div className="md:col-span-2">
            <label className="block text-[10px] font-black text-slate-800 mb-2 uppercase tracking-widest">Password *</label>
            <div className="relative">
              <input 
                type={showPassword ? "text" : "password"} 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Masukkan password" 
                className="w-full bg-white border border-slate-200 rounded-lg p-3 text-xs outline-none focus:border-blue-500 transition-all" 
              />
              <button 
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* Retype Password */}
          <div className="md:col-span-2">
            <label className="block text-[10px] font-black text-slate-800 mb-2 uppercase tracking-widest">Retype Password *</label>
            <div className="relative">
              <input 
                type={showConfirmPassword ? "text" : "password"} 
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Ulangi password" 
                className={`w-full bg-white border rounded-lg p-3 text-xs outline-none transition-all ${
                  confirmPassword && password !== confirmPassword 
                  ? "border-red-500 focus:border-red-500" 
                  : "border-slate-200 focus:border-blue-500"
                }`} 
              />
              <button 
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition-colors"
              >
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            
            {/* Pesan Error: Langsung hilang jika password === confirmPassword */}
            {confirmPassword && password !== confirmPassword && (
              <p className="text-[9px] text-red-500 font-bold italic mt-2 uppercase tracking-tighter">
                Password tidak cocok! Harap periksa kembali.
              </p>
            )}
          </div>

            {/* Submit Button */}
            <div className="md:col-span-2 pt-4">
              <button type="submit" className="w-full py-4 bg-[#5F85A6] hover:bg-[#4A6B8A] text-white font-black uppercase text-[11px] tracking-[0.2em] transition-all rounded-lg shadow-lg shadow-blue-900/10 active:scale-[0.98]">
                Create Account
              </button>
            </div>
          </form>
        </div>

        {/* RIGHT: INFO SECTION */}
        <div className="lg:col-span-4 space-y-6">
          <div className="p-6 rounded-2xl bg-slate-50 border border-slate-100 space-y-4">
            <h4 className="text-xs font-black text-[#003366] uppercase tracking-wider">Aplikasi Satu Sehat</h4>
            <p className="text-[11px] leading-relaxed text-slate-600">
              Bagi peserta yang belum memiliki akun Satu Sehat, silakan <span className="bg-yellow-200 px-1 font-bold text-slate-800">daftarkan diri Anda di aplikasi Satu Sehat</span> untuk mendapatkan sertifikat SKP.
            </p>
            <a 
              href="https://satusehat.kemkes.go.id/sdmk/register" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="block text-[10px] text-blue-600 font-bold hover:underline break-all"
            >
              https://satusehat.kemkes.go.id/sdmk/register
            </a>
          </div>
        </div>
      </div>

       {/* Footer */}
      <AuthFooter 
          question="Sudah memiliki akun?" 
          linkText="Login Sekarang" 
          linkTo="/login" 
        />
    </div>
  );
};

export default RegisterForm;